import React, { useState, useEffect, useCallback } from "react";
import Header from "../components/header";
import { Loader } from "../components/loader";
import UpdateHistoryList from "./updateHistoryList";
import UpdateStockDetails from "./updateStockDetails";
import CCMHome from "./ccmHome";
import "./home.css";
import { get } from "../config/api";
import {
  formatCycleInfoDate,
  parseAndNormalizeDate,
  parseAndNormalizeEndOfDay,
} from "../helper-functions";
import { setLoggedInUser } from "../helper-functions/userUtils";
import { HOME_TITLES, HOME_MESSAGES } from "../constants/textconstants";
import {
  RequestStockData,
  RequestUpdateHistory,
  UserDetails,
  StockCalendarInfo,
} from "../interfaces/types";
import { CirclePlus } from "../components/icons/circlePlus";
import { RightArrow } from "../components/icons/rightArrow";

export const Home: React.FC = () => {
  const [stockData, setStockData] = useState<RequestStockData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [userPersona, setUserPersona] = useState<"retailer" | "ccm">(
    "retailer"
  );
  const [updateHistoryState, setUpdateHistoryState] = useState<
    RequestUpdateHistory[] | null
  >(null);
  const [showHistoryPage, setShowHistoryPage] = useState<boolean>(false);
  const [showUpdateStockPage, setShowUpdateStockPage] =
    useState<boolean>(false);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [stockCalendarInfo, setStockCalendarInfo] =
    useState<StockCalendarInfo | null>(null);
  const [selectedEntryFromHome, setSelectedEntryFromHome] =
    useState<RequestUpdateHistory | null>(null);

  const getVisibleHistoryCount = () => {
    const windowHeight = window.innerHeight;
    if (windowHeight < 600) return 1;
    if (windowHeight < 800) return 2;
    return 3;
  };

  const visibleHistoryCount = getVisibleHistoryCount();
  const displayedHistory =
    updateHistoryState && updateHistoryState.length < 2
      ? updateHistoryState
      : updateHistoryState?.slice(0, visibleHistoryCount) || [];

  const fetchUserDetails = async (): Promise<UserDetails> => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const userId = urlParams.get("userId")?.toUpperCase();

      if (userId) {
        try {
          let response = await get(`/api/v1/customers/${userId}`);
          if (
            response &&
            (Array.isArray(response) ? response.length > 0 : response)
          ) {
            // Filter the response to find the user that matches the userId
            const apiUserData = Array.isArray(response)
              ? response.find((user: any) => user.userId === userId) ||
                response[0]
              : response;

            // Return user details from API response
            return {
              id: apiUserData.id || userId,
              name: apiUserData.name || "Unknown",
              classification: apiUserData.classification || "Unknown",
              subClassification: apiUserData.subClassification || "Unknown",
            };
          }
        } catch (apiError) {
          console.warn(
            "Failed to fetch user details from API, falling back to URL params:",
            apiError
          );
        }

        // Fallback to URL parameters if API fails
        const userDetails: UserDetails = {
          id: "Unknown",
          name: "Unknown",
          classification: "retailer",
          subClassification: "Unknown",
        };
        console.log("User details from query string:", userDetails);
        return userDetails;
      }
      console.log("No userId in query string, using fallback");
    } catch (error) {
      console.error("Error in fetchUserDetails:", error);
    }

    // Final fallback if no userId is available
    const fallbackUser: UserDetails = {
      id: "Unknown",
      name: "Unknown",
      classification: "retailer",
      subClassification: "Unknown",
    };
    console.log("Using fallback user details:", fallbackUser);
    return fallbackUser;
  };

  const fetchStockCalendarInfoByCountry = useCallback(
    async (countryCode: string): Promise<StockCalendarInfo | null> => {
      try {
        // Prepare query parameters
        const queryParams: any = { countryCode };

        // Fetch calendar/cycle information from the API based on country code
        const response = await get("/api/v1/stock-calender", queryParams);

        if (
          response &&
          (Array.isArray(response) ? response.length > 0 : response)
        ) {
          const calendarData = Array.isArray(response) ? response[0] : response;
          // Map API response to our StockCalendarInfo interface
          const stockCalendar: StockCalendarInfo = {
            currentCycleStartDate: calendarData.currentCycleStartDate || "",
            currentCycleEndDate: calendarData.currentCycleEndDate || "",
            currentCycleDueDate: calendarData.currentCycleDueDate || "",
            currentCycleRetailerDueDate:
              calendarData.currentCycleRetailerDueDate || "",
            nextCycleStartDate: calendarData.nextCycleStartDate || "",
          };
          return stockCalendar;
        } else {
          console.warn("No calendar info found for country:", countryCode);
          return null;
        }
      } catch (error) {
        console.error(
          "Error fetching stock calendar info for country:",
          countryCode,
          error
        );

        // Return fallback calendar info based on country code
        const fallbackCalendar: StockCalendarInfo = {
          currentCycleStartDate: new Date().toISOString(),
          currentCycleEndDate: new Date(
            Date.now() + 15 * 24 * 60 * 60 * 1000
          ).toISOString(), // 15 days from now
          currentCycleDueDate: new Date(
            Date.now() + 2 * 24 * 60 * 60 * 1000
          ).toISOString(), // 2 days from now
          currentCycleRetailerDueDate: new Date(
            Date.now() + 5 * 24 * 60 * 60 * 1000
          ).toISOString(), // 5 days from now
          nextCycleStartDate: new Date(
            Date.now() + 15 * 24 * 60 * 60 * 1000
          ).toISOString(), // 15 days from now
        };

        console.log(
          "Using fallback calendar info for country:",
          countryCode,
          fallbackCalendar
        );
        return fallbackCalendar;
      }
    },
    []
  ); // Empty dependency array since this function doesn't depend on any state or props

  useEffect(() => {
    const fetchRequests = async () => {
      setIsLoading(true);
      try {
        // Fetch user details first
        const userData = await fetchUserDetails();
        setUserDetails(userData);

        // Set userPersona based on user classification
        const persona =
          userData.classification?.toLowerCase() === "retailer"
            ? "retailer"
            : "ccm";
        setUserPersona(persona);

        // Always store userData.id as loggedInUser - accessible throughout the application
        setLoggedInUser(userData.id);

        // Get country code from URL parameters or use default
        const urlParams = new URLSearchParams(window.location.search);
        const countryCode = urlParams.get("countryCode") || "ID"; // Default to ID

        // Fetch stock calendar info based on country code
        const calendarInfo = await fetchStockCalendarInfoByCountry(countryCode);
        setStockCalendarInfo(calendarInfo);

        // Store calendarInfo in localStorage
        if (calendarInfo) {
          localStorage.setItem("calendarInfo", JSON.stringify(calendarInfo));
        }

        // Fetch product list for stock update
        let products: RequestStockData[] = [];
        try {
          const productsResponse = await get("/api/v1/products/corn-seeds", {
            countryCode,
          });
          if (productsResponse && Array.isArray(productsResponse)) {
            products = productsResponse.map((item: any) => ({
              productName: item.product_name,
              productDisplayName:
                item.product_display_name || item.product_name,
              stockQuantity: 0,
            }));
            setStockData(products);
          } else {
            setStockData([]);
          }
        } catch (error) {
          console.error("Error fetching products:", error);
          setStockData([]);
        }

        // Fetch dashboard data based on user details
        const response = await get(
          `/api/v1/customers/${userData.id}/product-stocks`
        );
        if (
          response &&
          (Array.isArray(response) ? response.length > 0 : response)
        ) {
          console.log("Dashboard data fetched successfully:", response);
          // Enrich with productDisplayName from products (now available)
          const enrichedResponse = enrichDashboardWithDisplayNames(
            response,
            products
          );
          setDashboardData(
            Array.isArray(enrichedResponse)
              ? enrichedResponse[0]
              : enrichedResponse
          );
          setUpdateHistoryState(enrichedResponse);
        } else {
          console.warn("No dashboard data found for user:", userData.id);
          setDashboardData([]);
          setUpdateHistoryState([]);
        }
      } catch (error) {
        console.error("Error fetching requests:", error);

        // Set fallback user data and empty arrays on error
        try {
          const fallbackUserData = await fetchUserDetails();
          setUserDetails(fallbackUserData);

          // Set userPersona based on fallback user classification
          const fallbackPersona =
            fallbackUserData.classification?.toLowerCase() === "retailer"
              ? "retailer"
              : "ccm";
          setUserPersona(fallbackPersona);

          // Always store fallback userData.id as loggedInUser
          setLoggedInUser(fallbackUserData.id);
          console.log(
            "LoggedInUser set for fallback user:",
            fallbackUserData.id
          );

          // Try to fetch calendar info even for fallback user
          try {
            const urlParams = new URLSearchParams(window.location.search);
            const countryCode = urlParams.get("countryCode") || "ID";
            const fallbackCalendarInfo = await fetchStockCalendarInfoByCountry(
              countryCode
            );
            setStockCalendarInfo(fallbackCalendarInfo);
          } catch (calendarError) {
            console.error(
              "Failed to fetch calendar info for fallback user:",
              calendarError
            );
            setStockCalendarInfo(null);
          }
        } catch (fallbackError) {
          console.error("Even fallback user details failed:", fallbackError);
        }
        setDashboardData([]);
        setUpdateHistoryState([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchRequests();
  }, [fetchStockCalendarInfoByCountry]); // Include the dependency to avoid React warnings

  // Helper function to enrich dashboard data with productDisplayName from stockData
  const enrichDashboardWithDisplayNames = (
    dashboardResponse: any,
    products: RequestStockData[]
  ) => {
    if (!dashboardResponse) return dashboardResponse;

    const enrichedResponse = Array.isArray(dashboardResponse)
      ? [...dashboardResponse]
      : { ...dashboardResponse };

    // Helper to enrich a single dashboard item
    const enrichItem = (item: any) => {
      if (item?.productStocks && Array.isArray(item.productStocks)) {
        return {
          ...item,
          productStocks: item.productStocks.map((stock: any) => {
            const matchingProduct = products.find(
              (p) => p.productName === stock.productName
            );
            return {
              ...stock,
              productDisplayName:
                stock.productDisplayName || matchingProduct?.productDisplayName,
            };
          }),
        };
      }
      return item;
    };

    if (Array.isArray(enrichedResponse)) {
      return enrichedResponse.map(enrichItem);
    } else {
      return enrichItem(enrichedResponse);
    }
  };

  const refreshDashboardData = async () => {
    console.log("Starting dashboard data refresh...");
    setIsLoading(true);

    try {
      if (userDetails?.id) {
        console.log("Refreshing dashboard data for user:", userDetails.id);

        // Fetch fresh dashboard data
        const response = await get(
          `/api/v1/customers/${userDetails.id}/product-stocks`
        );
        console.log("Refresh API response:", response);

        if (
          response &&
          (Array.isArray(response) ? response.length > 0 : response)
        ) {
          console.log("Dashboard data refreshed successfully:", response);
          // Enrich with productDisplayName from stockData
          const enrichedResponse = enrichDashboardWithDisplayNames(
            response,
            stockData
          );
          const newDashboardData = Array.isArray(enrichedResponse)
            ? enrichedResponse[0]
            : enrichedResponse;
          setDashboardData(newDashboardData);
          setUpdateHistoryState(enrichedResponse);
          console.log("State updated with fresh data");
        } else {
          console.warn(
            "No dashboard data found for user during refresh:",
            userDetails.id
          );
          setDashboardData([]);
          setUpdateHistoryState([]);
        }
      } else {
        console.error("No user details available for refresh");
      }
    } catch (error) {
      console.error("Error refreshing dashboard data:", error);
      // Don't clear existing data on error, just log it
    } finally {
      setIsLoading(false);
      console.log("Dashboard refresh completed");
    }
  };

  const handleBackClick = () => {
    // Handle back navigation
    console.log("Back button clicked");
  };

  const handleViewMore = () => {
    setShowHistoryPage(true);
  };

  const handleBackFromHistory = () => {
    setShowHistoryPage(false);
    setSelectedEntryFromHome(null); // Clear selected entry when going back
  };

  const handleUpdateStock = () => {
    setIsLoading(true);
    setShowUpdateStockPage(true);
    setIsLoading(false);
  };

  const handleBackFromUpdateStock = () => {
    setShowUpdateStockPage(false);
  };

  const handleSaveStockDetails = (
    updatedStockData: { productName: string; stockQuantity: string }[]
  ) => {
    setShowUpdateStockPage(false);
  };

  const handleEditStockDetails = () => {
    const products =
      dashboardData?.productStocks?.map((item: any) => ({
        productName: item.productName,
        productDisplayName: item.productDisplayName || item.productName,
        stockQuantity: item.stockQuantity || 0,
      })) || [];
    setStockData(products);
    setShowUpdateStockPage(true);
  };

  const handleEntryClick = (entry: RequestUpdateHistory, index: number) => {
    setSelectedEntryFromHome(entry);
    setShowHistoryPage(true);
  };

  return (
    <>
      {userPersona !== "retailer" ? (
        <CCMHome />
      ) : showUpdateStockPage ? (
        <UpdateStockDetails
          stockData={stockData}
          selectedDate={stockCalendarInfo?.currentCycleStartDate}
          onBack={handleBackFromUpdateStock}
          onSave={handleSaveStockDetails}
          onNavigateToHome={async () => {
            await refreshDashboardData();
            setShowUpdateStockPage(false);
          }}
          userRole={userPersona}
          customerId={userDetails?.id}
        />
      ) : showHistoryPage ? (
        <UpdateHistoryList
          data={updateHistoryState || []}
          onBack={handleBackFromHistory}
          onNavigateToHome={async () => {
            // Hide history page immediately to avoid flickering
            setShowHistoryPage(false);
            setSelectedEntryFromHome(null);
            // Then refresh data in background
            await refreshDashboardData();
          }}
          retailerId={userDetails?.id}
          userRole={userPersona}
          selectedEntryFromHome={selectedEntryFromHome}
        />
      ) : (
        <div className="page-container home-container">
          {isLoading && <Loader />}

          {/* Header Section: Corn Stock */}
          <Header
            title={HOME_TITLES.CORN_STOCK}
            onBackClick={handleBackClick}
            showBackIcon={false}
          />

          <div className="content-section">
            {/* Date Section */}
            <div className="date-section">
              <p className="date-text">
                {formatCycleInfoDate(
                  new Date().toString(),
                  "DDDD, MMM DD YYYY"
                )}
              </p>
            </div>

            {/* Green Thumb Growers Card */}
            <div className="info-card">
              <h2 className="card-title">{userDetails?.name}</h2>
              <p className="card-subtitle">
                {HOME_MESSAGES.ID_PREFIX} {userDetails?.id}
              </p>
            </div>

            <div className="overview-section-header">
              <span className="overview-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="9"
                  height="21"
                  viewBox="0 0 9 21"
                  fill="none"
                >
                  <path
                    d="M4.81184 0.430664C5.24527 0.725882 5.69645 1.16923 6.02348 1.58598C6.32652 1.97195 6.58158 2.42834 6.72919 2.89568C7.28259 2.70843 8.55839 2.32037 8.64758 3.22636C8.71643 3.92581 8.68722 5.29967 8.54118 5.9793C8.51301 6.11074 8.38053 6.39917 8.37948 6.48263C8.37844 6.53687 8.62254 6.73977 8.64758 7.0209C8.71069 7.73182 8.68983 9.01232 8.54848 9.70238C8.52031 9.84112 8.38053 10.1963 8.37948 10.2772C8.37844 10.3371 8.61889 10.5265 8.64602 10.7779C8.71643 11.4356 8.68931 12.8601 8.54848 13.4969C8.20267 15.0627 6.84915 16.363 5.24267 16.5482L5.23902 19.9672C5.20042 20.24 5.02256 20.3344 4.81236 20.4596H4.49941C4.27617 20.3281 4.11187 20.2092 4.07275 19.9281L4.0691 16.5482C2.18617 16.3046 0.78415 14.6898 0.664185 12.8136C0.629239 12.2655 0.616721 11.3548 0.664185 10.8154C0.6887 10.5406 0.793538 10.4769 0.939061 10.2704C0.783107 9.86615 0.6887 9.41394 0.664185 8.9805C0.635498 8.47404 0.602116 7.422 0.669401 6.94788C0.700696 6.72621 0.918197 6.57808 0.93228 6.50923C0.938539 6.47845 0.784671 6.01268 0.763286 5.90836C0.625588 5.2256 0.601073 3.96702 0.664185 3.26601C0.749725 2.31515 1.99162 2.70008 2.58206 2.8962C2.74949 2.40904 3.00767 1.95004 3.32688 1.54738C3.64609 1.14472 4.07536 0.713364 4.49888 0.430664H4.81184ZM5.66672 3.47516C5.53423 2.8503 5.14982 2.29116 4.66944 1.88067C4.15255 2.25934 3.81248 2.84822 3.63514 3.45638C4.04823 3.73335 4.3737 4.11828 4.63606 4.53815L5.16286 3.89139L5.66672 3.47516ZM4.06858 7.58943V6.31781C4.06858 6.1655 3.90793 5.68877 3.84117 5.52812C3.51048 4.73896 2.69837 4.03847 1.8388 3.91173V5.30019C1.8388 5.43059 1.94729 5.82334 1.99371 5.96678C2.03126 6.08309 2.20234 6.49462 2.28006 6.56034C2.35777 6.62606 2.96751 6.81644 3.1558 6.91711C3.49118 7.09601 3.78588 7.33803 4.06858 7.58891V7.58943ZM7.47193 3.91225C6.48196 4.07186 5.61299 4.92048 5.34855 5.87706C5.32351 5.96678 5.24214 6.29068 5.24214 6.35745V7.58943C5.55145 7.32864 5.86961 7.06315 6.23264 6.87799C6.40789 6.7888 6.95295 6.62763 7.04057 6.55148C7.0969 6.50245 7.232 6.20201 7.26746 6.11334C7.33579 5.94174 7.47245 5.5083 7.47245 5.34035V3.91225H7.47193ZM4.06858 11.384V10.1123C4.06858 9.98664 3.93349 9.53912 3.88028 9.4009C3.5376 8.51472 2.75783 7.88048 1.8388 7.66715V9.09525C1.8388 9.22252 1.94729 9.62205 1.99371 9.76184C2.02292 9.84946 2.23416 10.3199 2.27902 10.357C2.32387 10.394 2.63995 10.4613 2.73801 10.5046C3.22987 10.7216 3.6899 11.0011 4.06858 11.384ZM7.47193 9.13489C7.51678 8.66964 7.43854 8.14023 7.47193 7.66819C6.59462 7.88361 5.82215 8.48395 5.46956 9.3237C5.39236 9.50782 5.24214 9.96891 5.24214 10.1525V11.3845C5.7413 10.9005 6.35834 10.5447 7.03014 10.3559C7.19131 10.238 7.45106 9.35135 7.47193 9.13541V9.13489ZM4.06858 15.3741V13.9074C4.06858 13.8406 3.98721 13.5167 3.96217 13.427C3.69043 12.4438 2.83398 11.6625 1.8388 11.4622V12.8903C1.8388 13.0583 1.99423 13.5454 2.06621 13.7191C2.41411 14.5573 3.1897 15.1717 4.06858 15.3746V15.3741ZM7.47193 11.4622C6.55863 11.6505 5.76217 12.3233 5.43044 13.1954C5.37202 13.3483 5.24214 13.801 5.24214 13.946V15.3741C6.14657 15.1879 6.95347 14.5051 7.28363 13.6409C7.33318 13.5105 7.47193 13.0447 7.47193 12.9294V11.4627V11.4622Z"
                    fill="#007CBF"
                  />
                </svg>
              </span>
              <h3 className="overview-section-title">
                {HOME_TITLES.CORN_HYBRID_STOCK_OVERVIEW}
              </h3>
            </div>

            {/* Conditional button display based on cycle date */}
            {stockCalendarInfo?.currentCycleStartDate &&
            parseAndNormalizeDate(stockCalendarInfo?.currentCycleStartDate) <=
              new Date() &&
            new Date() <=
              parseAndNormalizeEndOfDay(
                stockCalendarInfo?.currentCycleRetailerDueDate
              ) &&
            (!dashboardData?.productStocks ||
              dashboardData?.productStocks?.length === 0 ||
              dashboardData?.stockCycle !==
                stockCalendarInfo?.currentCycleStartDate) ? (
              // Show editable button if cycle date is today or future and no stock data exists for current cycle
              <button className="add-stock-btn" onClick={handleUpdateStock}>
                <CirclePlus />
                {HOME_MESSAGES.UPDATE_STOCK_DETAILS_FOR}{" "}
                {formatCycleInfoDate(
                  stockCalendarInfo?.currentCycleStartDate,
                  "DD MMM"
                )}
              </button>
            ) : (
              // Show readonly button if cycle date is in the past
              <button className="view-stock-btn" disabled>
                <span className="view-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                  >
                    <path
                      d="M12.5 2C6.98 2 2.5 6.48 2.5 12C2.5 17.52 6.98 22 12.5 22C18.02 22 22.5 17.52 22.5 12C22.5 6.48 18.02 2 12.5 2ZM13.5 17H11.5V11H13.5V17ZM13.5 9H11.5V7H13.5V9Z"
                      fill="black"
                      fill-opacity="0.56"
                    />
                  </svg>
                </span>
                {HOME_MESSAGES.NEXT_STOCK_UPDATE_DUE_ON}
                <span className="view-date-text">
                  {formatCycleInfoDate(
                    stockCalendarInfo?.nextCycleStartDate,
                    "DD MMM"
                  )}
                </span>
              </button>
            )}

            {/* Check if both dashboardData and displayedHistory are not available */}
            {(!dashboardData?.productStocks ||
              dashboardData.productStocks.length === 0) &&
            (!displayedHistory || displayedHistory.length === 0) ? (
              /* Show combined no data message when both sections have no data */
              <div className="no-data-message-for-both-sections">
                <p className="no-data-text">
                  <b>
                    <span className="no-data-text-title">
                      {HOME_MESSAGES.NO_STOCK_UPDATES_FOUND}
                    </span>
                  </b>
                  <br />
                  {HOME_MESSAGES.STOCK_DETAILS_GUIDE}
                </p>
              </div>
            ) : (
              <>
                {/* Stock Details List */}
                <div className="stock-list">
                  {dashboardData?.productStocks &&
                  dashboardData.productStocks.length > 0 ? (
                    <>
                      <div className="lastupdate-section">
                        <div className="lastupdate-content">
                          <span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                            >
                              <path
                                d="M10.8333 2.5C6.69165 2.5 3.33331 5.85833 3.33331 10H0.833313L4.07498 13.2417L4.13331 13.3583L7.49998 10H4.99998C4.99998 6.775 7.60831 4.16667 10.8333 4.16667C14.0583 4.16667 16.6666 6.775 16.6666 10C16.6666 13.225 14.0583 15.8333 10.8333 15.8333C9.22498 15.8333 7.76665 15.175 6.71665 14.1167L5.53331 15.3C6.89165 16.6583 8.75831 17.5 10.8333 17.5C14.975 17.5 18.3333 14.1417 18.3333 10C18.3333 5.85833 14.975 2.5 10.8333 2.5ZM9.99998 6.66667V10.8333L13.5666 12.95L14.1666 11.9417L11.25 10.2083V6.66667H9.99998Z"
                                fill="black"
                                fill-opacity="0.56"
                              />
                            </svg>
                          </span>
                          <p className="lastupdate-text">
                            {HOME_MESSAGES.LAST_UPDATED_ON}{" "}
                            {formatCycleInfoDate(
                              dashboardData?.stockUpdatedAt,
                              "MMM DD YYYY"
                            )}
                          </p>
                        </div>
                        {/* Show edit-icon only when add-stock-btn is disabled (i.e., when view-stock-btn is shown) */}
                        {stockCalendarInfo?.currentCycleStartDate &&
                          parseAndNormalizeDate(
                            stockCalendarInfo?.currentCycleStartDate
                          ) <= new Date() &&
                          new Date() <=
                            parseAndNormalizeEndOfDay(
                              stockCalendarInfo?.currentCycleRetailerDueDate
                            ) &&
                          dashboardData?.isEditable === true && (
                            <p
                              className="edit-icon"
                              onClick={handleEditStockDetails}
                            >
                              <span>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M2.99878 17.2513V21.0013H6.74878L17.8088 9.94128L14.0588 6.19128L2.99878 17.2513ZM20.7088 7.04128C21.0988 6.65128 21.0988 6.02128 20.7088 5.63128L18.3688 3.29128C17.9788 2.90128 17.3488 2.90128 16.9588 3.29128L15.1288 5.12128L18.8788 8.87128L20.7088 7.04128Z"
                                    fill="#007CBF"
                                  />
                                </svg>
                              </span>
                            </p>
                          )}
                      </div>
                      {dashboardData.productStocks.map((item: any) => (
                        <div key={item.productName} className="stock-item-row">
                          <span className="stock-id">
                            {item.productDisplayName || item.productName}
                          </span>
                          <span className="stock-quantity">
                            {item.stockQuantity} {HOME_MESSAGES.KGS}
                          </span>
                        </div>
                      ))}
                      <div className="home-stock-total">
                        <span>{HOME_MESSAGES.TOTAL}</span>
                        <span>
                          {dashboardData?.totalAvailableStock}{" "}
                          {HOME_MESSAGES.KGS}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="no-data-message">
                      <div className="no-data-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="48"
                          height="48"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M3 3l18 18" />
                          <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2H9" />
                          <path d="M7 7V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" />
                        </svg>
                      </div>
                      <p className="no-data-text">
                        {HOME_MESSAGES.NO_DATA_FOUND_USER}
                      </p>
                    </div>
                  )}
                </div>

                {/* Update History */}
                <div>
                  <h3 className="section-title">
                    {HOME_TITLES.UPDATE_HISTORY}
                  </h3>
                  {displayedHistory && displayedHistory.length > 0 ? (
                    <>
                      <div className="update-history-grid">
                        {displayedHistory.map((entry: any, index: number) => (
                          <div
                            key={index}
                            className="update-history-card"
                            onClick={() => handleEntryClick(entry, index)}
                            style={{ cursor: "pointer" }}
                          >
                            <div className="update-date">
                              {formatCycleInfoDate(entry.stockCycle, "DD MMM")}
                            </div>
                            <div className="update-quantity">
                              {entry.totalAvailableStock} {HOME_MESSAGES.KGS}
                            </div>
                          </div>
                        ))}
                      </div>
                      {/* Always show View More button when there's data */}
                      <button
                        className="view-more-btn"
                        onClick={handleViewMore}
                      >
                        {HOME_MESSAGES.VIEW_MORE} <RightArrow />
                      </button>
                    </>
                  ) : (
                    <div className="no-data-message">
                      <div className="no-data-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="48"
                          height="48"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M3 3l18 18" />
                          <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2H9" />
                          <path d="M7 7V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" />
                        </svg>
                      </div>
                      <p className="no-data-text">
                        {HOME_MESSAGES.NO_UPDATE_HISTORY_FOUND}
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Home;
